// Trello-style card detail modal
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../shared/api/client";
import { I } from "../../shared/Icon";
import { toast } from "../../shared/stores/toasts";
import { useAuth } from "../../shared/stores/auth";
import { OrgPicker } from "../../shared/org/OrgPicker";
import { useOrg } from "../../shared/org/useOrg";

// ── Types ─────────────────────────────────────────────────────────────────────

export type CheckItem = { id: number; title: string; is_done: boolean; sort_order: number };
export type Checklist = { id: number; title: string; sort_order: number; items: CheckItem[] };
export type LabelInfo = { id: number; name: string; color: string };
export type Comment   = { id: number; user_id: number; content: string; created_at: string };

export type TodoFull = {
  id: number;
  user_id: number;
  title: string;
  note: string | null;
  is_done: boolean;
  start_date: string | null;
  due_date: string | null;
  start_time: string | null;
  due_time: string | null;
  priority: string;
  kanban_status: string;
  status: string;
  category: string;
  scope: string;
  page_id: number | null;
  cover_color: string | null;
  is_archived: boolean;
  assignee_ids: number[];
  label_ids: number[];
  checklist_summary: { total: number; done: number };
  labels: LabelInfo[];
  checklists: Checklist[];
  comments: Comment[];
  created_at: string;
};

type DirUser = { id: number; name: string; department_name: string | null };

// ── Helpers ───────────────────────────────────────────────────────────────────

const COVER_COLORS = [
  "#EF4444","#F97316","#EAB308","#22C55E","#06B6D4",
  "#3B82F6","#8B5CF6","#EC4899","#6B7280","#0F172A",
];

const STATUS_OPTS = [
  { id: "inbox", label: "수신함",    color: "#94A3B8" },
  { id: "today", label: "오늘 할 일", color: "#3B82F6" },
  { id: "doing", label: "진행 중",   color: "#F97316" },
  { id: "wait",  label: "대기 중",   color: "#8B5CF6" },
  { id: "hold",  label: "보류",      color: "#EAB308" },
  { id: "delay", label: "지연",      color: "#EF4444" },
  { id: "done",  label: "완료",      color: "#22C55E" },
  { id: "cancel", label: "취소",     color: "#6B7280" },
];

function avatarColor(id: number) {
  const p = ["#6366f1","#0891b2","#d97706","#16a34a","#dc2626","#7c3aed","#db2777","#0d9488"];
  return p[id % p.length];
}
// 카테고리 이름 → 고정 색상(해시)
const CAT_PALETTE = ["#6366f1","#0891b2","#d97706","#16a34a","#dc2626","#7c3aed","#db2777","#0d9488","#ea580c","#0ea5e9"];
function catColor(name: string) {
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return CAT_PALETTE[h % CAT_PALETTE.length];
}
// #RRGGBB + 알파(00~FF) → 연한 배경
function tint(hex: string, alpha: string) { return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex + alpha : hex; }
// 오늘 날짜/현재 시각(작업 시간 기준) 문자열
function todayStr() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function nowTimeStr() { const d = new Date(); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; }
function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length-1][0]).toUpperCase() : name.slice(0,2).toUpperCase();
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
      <span style={{ fontSize: 15 }}>{icon}</span>
      <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text-1)" }}>{title}</span>
    </div>
  );
}

function ProgressBar({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 11, color: "var(--text-3)", minWidth: 32 }}>{pct}%</span>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: "var(--border)" }}>
        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 3, background: pct === 100 ? "#22C55E" : "var(--p-500, #6366f1)", transition: "width .2s" }} />
      </div>
      <span style={{ fontSize: 11, color: "var(--text-3)" }}>{done}/{total}</span>
    </div>
  );
}

// ── ChecklistSection ──────────────────────────────────────────────────────────

function ChecklistSection({
  todoId, checklist, onUpdate, onDelete,
}: {
  todoId: number;
  checklist: Checklist;
  onUpdate: (cl: Checklist) => void;
  onDelete: () => void;
}) {
  const [newItem, setNewItem] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const [clTitle, setClTitle] = useState(checklist.title);
  const inputRef = useRef<HTMLInputElement>(null);

  const done  = checklist.items.filter(it => it.is_done).length;
  const total = checklist.items.length;

  const addItem = async () => {
    const t = newItem.trim();
    if (!t) return;
    try {
      const res = await api.post<CheckItem>(
        `/todos/${todoId}/checklists/${checklist.id}/items`,
        { title: t, sort_order: checklist.items.length },
      );
      onUpdate({ ...checklist, items: [...checklist.items, res.data] });
      setNewItem("");
      inputRef.current?.focus();
    } catch { toast.error("항목 추가 실패"); }
  };

  const toggleItem = async (item: CheckItem) => {
    try {
      const res = await api.patch<CheckItem>(
        `/todos/${todoId}/checklists/${checklist.id}/items/${item.id}`,
        { is_done: !item.is_done },
      );
      onUpdate({ ...checklist, items: checklist.items.map(it => it.id === item.id ? res.data : it) });
    } catch { toast.error("항목 변경 실패"); }
  };

  const deleteItem = async (itemId: number) => {
    try {
      await api.delete(`/todos/${todoId}/checklists/${checklist.id}/items/${itemId}`);
      onUpdate({ ...checklist, items: checklist.items.filter(it => it.id !== itemId) });
    } catch { toast.error("항목 삭제 실패"); }
  };

  const saveClTitle = async () => {
    if (!clTitle.trim()) { setClTitle(checklist.title); setEditingTitle(false); return; }
    try {
      await api.patch(`/todos/${todoId}/checklists/${checklist.id}`, { title: clTitle.trim(), sort_order: checklist.sort_order });
      onUpdate({ ...checklist, title: clTitle.trim() });
      setEditingTitle(false);
    } catch { toast.error("저장 실패"); }
  };

  const deleteCl = async () => {
    if (!confirm(`"${checklist.title}" 체크리스트를 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/todos/${todoId}/checklists/${checklist.id}`);
      onDelete();
    } catch { toast.error("삭제 실패"); }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Title row */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        {editingTitle ? (
          <input
            autoFocus
            value={clTitle}
            onChange={e => setClTitle(e.target.value)}
            onBlur={saveClTitle}
            onKeyDown={e => { if (e.key === "Enter") saveClTitle(); if (e.key === "Escape") { setClTitle(checklist.title); setEditingTitle(false); } }}
            style={{ flex: 1, fontSize: 13, fontWeight: 600, background: "var(--bg-1)", border: "1px solid var(--p-400)", borderRadius: 4, padding: "3px 7px", color: "var(--text-1)" }}
          />
        ) : (
          <span onClick={() => setEditingTitle(true)} style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--text-1)", cursor: "pointer" }}>
            {checklist.title}
          </span>
        )}
        <button onClick={deleteCl} style={{ fontSize: 11, padding: "2px 8px", border: "1px solid var(--border)", borderRadius: 4, background: "none", color: "var(--text-3)", cursor: "pointer" }}>삭제</button>
      </div>

      {total > 0 && <ProgressBar done={done} total={total} />}

      {/* Items */}
      <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
        {checklist.items.map(item => (
          <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", borderRadius: 4, background: item.is_done ? "var(--bg-1)" : "transparent" }}
            onMouseEnter={e => { if (!item.is_done) (e.currentTarget as HTMLElement).style.background = "var(--hover, #f3f4f6)"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = item.is_done ? "var(--bg-1)" : "transparent"; }}
          >
            <input type="checkbox" checked={item.is_done} onChange={() => toggleItem(item)}
              style={{ accentColor: "var(--p-500, #6366f1)", flexShrink: 0, cursor: "pointer" }} />
            <span style={{ flex: 1, fontSize: 13, color: item.is_done ? "var(--text-3)" : "var(--text-1)", textDecoration: item.is_done ? "line-through" : "none" }}>
              {item.title}
            </span>
            <button onClick={() => deleteItem(item.id)} style={{ opacity: 0, fontSize: 11, padding: "1px 5px", border: "none", background: "none", color: "var(--text-3)", cursor: "pointer", borderRadius: 3 }}
              onMouseEnter={e => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={e => (e.currentTarget.style.opacity = "0")}
            >✕</button>
          </div>
        ))}
      </div>

      {/* Add item */}
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <input
          ref={inputRef}
          value={newItem}
          onChange={e => setNewItem(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") addItem(); }}
          placeholder="항목 추가..."
          style={{
            flex: 1, fontSize: 12, padding: "5px 9px", border: "1px solid var(--border)", borderRadius: 5,
            background: "var(--bg-0)", color: "var(--text-1)", fontFamily: "inherit",
          }}
        />
        <button onClick={addItem} style={{ fontSize: 12, padding: "5px 12px", borderRadius: 5, border: "none", background: "var(--p-500, #6366f1)", color: "#fff", cursor: "pointer", fontWeight: 600 }}>추가</button>
      </div>
    </div>
  );
}

// ── CommentsSection ───────────────────────────────────────────────────────────

function CommentsSection({
  todoId, comments, users, currentUserId, onUpdate,
}: {
  todoId: number;
  comments: Comment[];
  users: DirUser[];
  currentUserId: number;
  onUpdate: (comments: Comment[]) => void;
}) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      const res = await api.post<Comment>(`/todos/${todoId}/comments`, { content: t });
      onUpdate([...comments, res.data]);
      setText("");
    } catch { toast.error("댓글 추가 실패"); }
    finally { setSaving(false); }
  };

  const del = async (id: number) => {
    try {
      await api.delete(`/todos/${todoId}/comments/${id}`);
      onUpdate(comments.filter(c => c.id !== id));
    } catch { toast.error("댓글 삭제 실패"); }
  };

  return (
    <div>
      {comments.map(c => {
        const u = users.find(x => x.id === c.user_id);
        const isMe = c.user_id === currentUserId;
        return (
          <div key={c.id} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: avatarColor(c.user_id), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {u ? initials(u.name) : "?"}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-1)" }}>{u?.name ?? `#${c.user_id}`}</span>
                <span style={{ fontSize: 10, color: "var(--text-3)" }}>{new Date(c.created_at).toLocaleString("ko-KR", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                {isMe && (
                  <button onClick={() => del(c.id)} style={{ fontSize: 10, border: "none", background: "none", color: "var(--text-3)", cursor: "pointer", padding: 0, marginLeft: 4 }}>삭제</button>
                )}
              </div>
              <div style={{ fontSize: 13, color: "var(--text-1)", background: "var(--bg-1)", borderRadius: 6, padding: "7px 10px", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                {c.content}
              </div>
            </div>
          </div>
        );
      })}
      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
          placeholder="댓글 작성... (Ctrl+Enter로 전송)"
          rows={2}
          style={{ flex: 1, fontSize: 13, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--bg-0)", color: "var(--text-1)", fontFamily: "inherit", resize: "vertical" }}
        />
        <button onClick={submit} disabled={saving || !text.trim()} style={{ fontSize: 12, padding: "8px 14px", borderRadius: 6, border: "none", background: "var(--p-500, #6366f1)", color: "#fff", cursor: "pointer", fontWeight: 600, alignSelf: "flex-end", opacity: saving || !text.trim() ? 0.5 : 1 }}>전송</button>
      </div>
    </div>
  );
}

// ── Main CardDetail ───────────────────────────────────────────────────────────

export function CardDetail({
  todoId, users, categories = [], onClose, onChange,
}: {
  todoId: number;
  users: DirUser[];
  categories?: string[];
  onClose: () => void;
  onChange: (partial: Partial<TodoFull> & { id: number }) => void;
}) {
  const { user: authUser } = useAuth();
  const currentUserId = authUser?.id ?? 0;
  const { users: orgUsers } = useOrg();
  // 담당자 이름 해석/검색용 — 전달받은 users + 조직 전체 인원(size=500) 병합
  const dir = useMemo<DirUser[]>(() => {
    const m = new Map<number, DirUser>();
    for (const u of users) m.set(u.id, u);
    for (const u of orgUsers) if (!m.has(u.id)) m.set(u.id, { id: u.id, name: u.name, department_name: u.department_name ?? null });
    return Array.from(m.values());
  }, [users, orgUsers]);

  const [detail, setDetail] = useState<TodoFull | null>(null);
  const [labels, setLabels]   = useState<LabelInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCover, setShowCover]       = useState(false);
  const [showLabels, setShowLabels]     = useState(false);
  const [showAssignees, setShowAssignees] = useState(false);
  const [showOrgPicker, setShowOrgPicker] = useState(false);
  const [assigneeQ, setAssigneeQ]       = useState("");
  const [showAddCl, setShowAddCl]       = useState(false);
  const [newClTitle, setNewClTitle]     = useState("체크리스트");
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft]     = useState("");
  const [editingNote, setEditingNote]   = useState(false);
  const [noteDraft, setNoteDraft]       = useState("");
  const [draft, setDraft]               = useState<Partial<TodoFull>>({});
  const [savingCard, setSavingCard]     = useState(false);

  useEffect(() => {
    Promise.all([
      api.get<TodoFull>(`/todos/${todoId}`),
      api.get<LabelInfo[]>("/todos/labels"),
    ]).then(([detailRes, labelsRes]) => {
      const d = detailRes.data;
      // 시작 일시가 비어 있으면 오늘(작업 시간 기준)로 기본 셋팅 — 저장 시 반영됨
      if (!d.start_date) {
        const ds = todayStr(), ts = nowTimeStr();
        d.start_date = ds; d.start_time = ts;
        setDraft({ start_date: ds, start_time: ts });
      }
      setDetail(d);
      setLabels(labelsRes.data ?? []);
    }).catch(() => toast.error("불러오기 실패"))
      .finally(() => setLoading(false));
  }, [todoId]);

  // close on Escape
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape" && !editingTitle && !editingNote) onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose, editingTitle, editingNote]);

  if (loading || !detail) {
    return (
      <div className="t-overlay" onClick={onClose}>
        <div className="t-cmdk" style={{ width: 720, maxWidth: "95vw", padding: 32, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
          불러오는 중…
        </div>
      </div>
    );
  }

  const isOwner = detail.user_id === currentUserId;

  // 편집은 로컬 드래프트에만 반영 — '저장' 버튼을 눌러야 실제 저장됨(딴 곳 클릭·Esc 시 폐기)
  const patch = (body: Record<string, unknown>) => {
    setDraft(d => ({ ...d, ...body }));
    setDetail(prev => prev ? { ...prev, ...body } as TodoFull : prev);
  };
  const saveCard = async () => {
    if (Object.keys(draft).length === 0) { onClose(); return; }
    setSavingCard(true);
    try {
      const res = await api.patch<TodoFull>(`/todos/${todoId}`, draft);
      onChange({ ...res.data, id: todoId });
      onClose();
    } catch { toast.error("저장 실패"); setSavingCard(false); }
  };

  // Cover
  const setCover = (color: string | null) => patch({ cover_color: color });

  // 담당자(멤버) — 조직 보드는 누구나, 개인 보드는 작성자만 변경 가능
  const canAssign = isOwner || detail.scope !== "PERSONAL";
  const applyAssignees = (ids: number[]) => patch({ assignee_ids: ids });
  const addAssignee = (uid: number) => {
    if (detail.assignee_ids.includes(uid)) return;
    applyAssignees([...detail.assignee_ids, uid]);
  };
  const removeAssignee = (uid: number) => applyAssignees(detail.assignee_ids.filter(x => x !== uid));

  // Labels
  const toggleLabel = async (labelId: number) => {
    const has = detail.label_ids.includes(labelId);
    try {
      if (has) {
        await api.delete(`/todos/${todoId}/labels/${labelId}`);
        const next = detail.label_ids.filter(x => x !== labelId);
        const nextLabels = detail.labels.filter(l => l.id !== labelId);
        setDetail(prev => prev ? { ...prev, label_ids: next, labels: nextLabels } : prev);
        onChange({ id: todoId, label_ids: next });
      } else {
        await api.post(`/todos/${todoId}/labels/${labelId}`);
        const lb = labels.find(l => l.id === labelId)!;
        const next = [...detail.label_ids, labelId];
        const nextLabels = [...detail.labels, lb];
        setDetail(prev => prev ? { ...prev, label_ids: next, labels: nextLabels } : prev);
        onChange({ id: todoId, label_ids: next });
      }
    } catch { toast.error("라벨 변경 실패"); }
  };
  const renameLabel = async (lb: LabelInfo) => {
    const nn = prompt("라벨 이름 수정", lb.name)?.trim();
    if (!nn || nn === lb.name) return;
    try {
      await api.patch(`/todos/labels/${lb.id}`, { name: nn });
      setLabels(prev => prev.map(x => x.id === lb.id ? { ...x, name: nn } : x));
      setDetail(prev => prev ? { ...prev, labels: prev.labels.map(x => x.id === lb.id ? { ...x, name: nn } : x) } : prev);
    } catch { toast.error("라벨 이름 변경 실패"); }
  };

  // Checklists
  const addChecklist = async () => {
    const t = newClTitle.trim();
    if (!t) return;
    try {
      const res = await api.post<Checklist>(`/todos/${todoId}/checklists`, { title: t, sort_order: detail.checklists.length });
      setDetail(prev => prev ? { ...prev, checklists: [...prev.checklists, res.data] } : prev);
      setNewClTitle("체크리스트");
      setShowAddCl(false);
    } catch { toast.error("체크리스트 추가 실패"); }
  };

  const updateChecklist = (updated: Checklist) => {
    // If items are empty and this was triggered by deletion of the whole list, remove it
    setDetail(prev => {
      if (!prev) return prev;
      const exists = prev.checklists.find(c => c.id === updated.id);
      if (!exists) return prev;
      // Detect full-delete by checking if API responded (we pass empty items as signal)
      const newList = prev.checklists.map(c => c.id === updated.id ? updated : c);
      return { ...prev, checklists: newList };
    });
  };

  const removeChecklist = (clId: number) => {
    setDetail(prev => prev ? { ...prev, checklists: prev.checklists.filter(c => c.id !== clId) } : prev);
  };

  // Archive
  const toggleArchive = () => patch({ is_archived: !detail.is_archived });
  const removeCard = async () => {
    if (!confirm("이 카드를 삭제하시겠습니까? 되돌릴 수 없습니다.")) return;
    try {
      await api.delete(`/todos/${todoId}`);
      window.dispatchEvent(new Event("todos-board-refresh"));
      window.dispatchEvent(new Event("todos-inbox-refresh"));
      onClose();
    } catch { toast.error("삭제 실패"); }
  };

  // Title save
  const saveTitle = async () => {
    const t = titleDraft.trim();
    if (!t || t === detail.title) { setEditingTitle(false); return; }
    await patch({ title: t });
    setEditingTitle(false);
  };

  // Note save
  const saveNote = async () => {
    if (noteDraft === (detail.note ?? "")) { setEditingNote(false); return; }
    await patch({ note: noteDraft || null });
    setEditingNote(false);
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div
        className="t-cmdk"
        onClick={e => e.stopPropagation()}
        style={{ width: 720, maxWidth: "95vw", padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "90vh" }}
      >
        {/* Cover */}
        {detail.cover_color && (
          <div style={{ height: 80, background: detail.cover_color, flexShrink: 0, position: "relative" }}>
            <button onClick={() => setCover(null)} style={{ position: "absolute", top: 8, right: 8, fontSize: 11, padding: "3px 10px", border: "none", borderRadius: 4, background: "rgba(0,0,0,0.35)", color: "#fff", cursor: "pointer" }}>커버 제거</button>
          </div>
        )}

        {/* Header */}
        <div style={{ padding: "16px 20px 10px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          {editingTitle ? (
            <input
              autoFocus
              value={titleDraft}
              onChange={e => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={e => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setEditingTitle(false); }}
              style={{ width: "100%", fontSize: 16, fontWeight: 700, border: "none", borderBottom: "2px solid var(--p-500)", background: "transparent", color: "var(--text-1)", outline: "none", fontFamily: "inherit", paddingBottom: 2 }}
            />
          ) : (
            <h2
              onClick={() => { setTitleDraft(detail.title); setEditingTitle(true); }}
              style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--text-1)", cursor: "pointer", lineHeight: 1.4 }}
              title="클릭하여 수정"
            >
              {detail.title}
            </h2>
          )}
          {detail.is_archived && (
            <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#D97706", background: "#FEF3C7", borderRadius: 4, padding: "2px 8px" }}>
              📦 보관됨
            </div>
          )}
          <button onClick={onClose} style={{ position: "absolute", top: 14, right: 16, background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "var(--text-3)", lineHeight: 1 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* Main content */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

            {/* 진행상황 — 둥근 사각형 칩 선택 */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>진행상황</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {STATUS_OPTS.map(s => {
                  const on = (detail.status || detail.kanban_status) === s.id;
                  return (
                    <button key={s.id} onClick={() => patch({ status: s.id, is_done: s.id === "done" })}
                      style={{ fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 8, cursor: "pointer",
                        border: `1.5px solid ${on ? s.color : tint(s.color, "55")}`, background: on ? s.color : tint(s.color, "1A"),
                        color: on ? "#fff" : s.color }}>{s.label}</button>
                  );
                })}
              </div>
            </div>

            {/* 카테고리(카드 리스트) — 둥근 사각형 칩 선택 */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>카테고리 (카드 리스트)</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(() => {
                  const cats = Array.from(new Set([...(detail.category ? [detail.category] : []), ...categories]));
                  const chip = (val: string, label: string) => {
                    const on = (detail.category || "") === val;
                    if (!val) {  // (없음) — 무채색
                      return (
                        <button key="__none__" onClick={() => patch({ category: "" })}
                          style={{ fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 8, cursor: "pointer",
                            border: `1.5px solid ${on ? "var(--text-2)" : "var(--border)"}`, background: on ? "var(--bg-2,#e5e7eb)" : "transparent",
                            color: "var(--text-2)" }}>{label}</button>
                      );
                    }
                    const c = catColor(val);
                    return (
                      <button key={val} onClick={() => patch({ category: val })}
                        style={{ fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 8, cursor: "pointer",
                          border: `1.5px solid ${on ? c : tint(c, "55")}`, background: on ? c : tint(c, "1A"),
                          color: on ? "#fff" : c }}>{label}</button>
                    );
                  };
                  return [chip("", "(없음)"), ...cats.map(c => chip(c, c))];
                })()}
              </div>
            </div>

            {/* 일정 (기간) */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>일정 (시작 ~ 마감)</div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <input className="t-input" type="date" value={detail.start_date || ""} onChange={e => patch({ start_date: e.target.value || null })} style={{ fontSize: 13, padding: "5px 7px" }} />
                <input className="t-input" type="time" value={detail.start_time || ""} onChange={e => patch({ start_time: e.target.value || null })} style={{ fontSize: 13, padding: "5px 7px", width: 100 }} />
                <span style={{ color: "var(--text-3)" }}>~</span>
                <input className="t-input" type="date" value={detail.due_date || ""} onChange={e => patch({ due_date: e.target.value || null })} style={{ fontSize: 13, padding: "5px 7px" }} />
                <input className="t-input" type="time" value={detail.due_time || ""} onChange={e => patch({ due_time: e.target.value || null })} style={{ fontSize: 13, padding: "5px 7px", width: 100 }} />
              </div>
            </div>

            {/* 담당자 (멤버) */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>담당자</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                {detail.assignee_ids.map(uid => {
                  const u = dir.find(x => x.id === uid);
                  return (
                    <span key={uid} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px 3px 3px", borderRadius: 14, background: "var(--bg-1)", border: "1px solid var(--border)" }}>
                      <span style={{ width: 22, height: 22, borderRadius: "50%", background: avatarColor(uid), color: "#fff", fontSize: 9, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                        {u ? initials(u.name) : "?"}
                      </span>
                      <span style={{ fontSize: 12, color: "var(--text-1)" }}>{u?.name ?? `#${uid}`}</span>
                      {canAssign && (
                        <button onClick={() => removeAssignee(uid)} title="제거" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 12, padding: "0 2px", lineHeight: 1 }}>✕</button>
                      )}
                    </span>
                  );
                })}
                {canAssign && (
                  <button onClick={() => setShowOrgPicker(true)} title="조직도에서 선택"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 11px", borderRadius: 14, border: "1px dashed var(--border)", background: "transparent", color: "var(--text-2)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    🏢 조직도
                  </button>
                )}
                {canAssign && (
                  <div style={{ position: "relative" }}>
                    <button onClick={() => setShowAssignees(v => !v)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 11px", borderRadius: 14, border: "1px dashed var(--border)", background: "transparent", color: "var(--text-2)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                      <I.Plus size={12} /> 검색
                    </button>
                    {showAssignees && (
                      <div style={{ position: "absolute", top: 34, left: 0, zIndex: 50, width: 260, background: "var(--bg-0)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 10px 30px rgba(0,0,0,0.18)", padding: 8 }}>
                        <input className="t-input" autoFocus placeholder="이름·아이디 검색…" value={assigneeQ} onChange={e => setAssigneeQ(e.target.value)} style={{ marginBottom: 6, fontSize: 13 }} />
                        <div style={{ maxHeight: 220, overflowY: "auto" }}>
                          {dir.filter(u => !assigneeQ || u.name.includes(assigneeQ) || u.department_name?.includes(assigneeQ)).slice(0, 40).map(u => {
                            const on = detail.assignee_ids.includes(u.id);
                            return (
                              <button key={u.id} onClick={() => { on ? removeAssignee(u.id) : addAssignee(u.id); }}
                                style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "6px 8px", border: "none", background: on ? "var(--p-50, #eef2ff)" : "transparent", cursor: "pointer", borderRadius: 6, textAlign: "left" }}>
                                <span style={{ width: 26, height: 26, borderRadius: "50%", background: avatarColor(u.id), color: "#fff", fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{initials(u.name)}</span>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ display: "block", fontSize: 13, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</span>
                                  <span style={{ display: "block", fontSize: 11, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.department_name ?? ""}</span>
                                </span>
                                {on && <span style={{ color: "var(--p-500)", fontSize: 14, flexShrink: 0 }}>✓</span>}
                              </button>
                            );
                          })}
                          {dir.filter(u => !assigneeQ || u.name.includes(assigneeQ) || u.department_name?.includes(assigneeQ)).length === 0 && (
                            <div style={{ padding: "10px 8px", fontSize: 12, color: "var(--text-3)", textAlign: "center" }}>검색 결과가 없습니다</div>
                          )}
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                          <button onClick={() => setShowAssignees(false)} style={{ fontSize: 12, padding: "4px 12px", border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--text-2)", cursor: "pointer" }}>완료</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {detail.assignee_ids.length === 0 && !canAssign && (
                  <span style={{ fontSize: 12, color: "var(--text-3)" }}>지정된 담당자 없음</span>
                )}
              </div>
            </div>

            {/* Labels */}
            {detail.labels.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", marginBottom: 5, textTransform: "uppercase", letterSpacing: .5 }}>라벨</div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {detail.labels.map(lb => (
                    <span key={lb.id} style={{ padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: lb.color, color: "#fff" }}>
                      {lb.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Note */}
            <div style={{ marginBottom: 18 }}>
              <SectionHeader icon="📝" title="설명" />
              {editingNote ? (
                <div>
                  <textarea
                    autoFocus
                    value={noteDraft}
                    onChange={e => setNoteDraft(e.target.value)}
                    rows={4}
                    style={{ width: "100%", fontSize: 13, padding: "8px 10px", border: "1px solid var(--p-400)", borderRadius: 6, background: "var(--bg-0)", color: "var(--text-1)", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
                  />
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    <button onClick={saveNote} style={{ fontSize: 12, padding: "5px 14px", border: "none", borderRadius: 5, background: "var(--p-500, #6366f1)", color: "#fff", cursor: "pointer", fontWeight: 600 }}>저장</button>
                    <button onClick={() => setEditingNote(false)} style={{ fontSize: 12, padding: "5px 12px", border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--text-2)", cursor: "pointer" }}>취소</button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => { setNoteDraft(detail.note ?? ""); setEditingNote(true); }}
                  style={{ fontSize: 13, color: detail.note ? "var(--text-1)" : "var(--text-3)", background: "var(--bg-1)", borderRadius: 6, padding: "10px 12px", cursor: "pointer", minHeight: 44, lineHeight: 1.6, whiteSpace: "pre-wrap" }}
                >
                  {detail.note || "설명 추가..."}
                </div>
              )}
            </div>

            {/* Checklists */}
            {detail.checklists.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <SectionHeader icon="☑️" title="체크리스트" />
                {detail.checklists.map(cl => (
                  <ChecklistSection
                    key={cl.id}
                    todoId={todoId}
                    checklist={cl}
                    onUpdate={updateChecklist}
                    onDelete={() => removeChecklist(cl.id)}
                  />
                ))}
              </div>
            )}

            {/* Add checklist */}
            {showAddCl ? (
              <div style={{ marginBottom: 18, background: "var(--bg-1)", borderRadius: 8, padding: 12, border: "1px solid var(--border)" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>체크리스트 이름</div>
                <input
                  autoFocus
                  value={newClTitle}
                  onChange={e => setNewClTitle(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") addChecklist(); if (e.key === "Escape") setShowAddCl(false); }}
                  style={{ width: "100%", fontSize: 13, padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 5, background: "var(--bg-0)", color: "var(--text-1)", fontFamily: "inherit", boxSizing: "border-box" }}
                />
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <button onClick={addChecklist} style={{ fontSize: 12, padding: "5px 14px", border: "none", borderRadius: 5, background: "var(--p-500, #6366f1)", color: "#fff", cursor: "pointer", fontWeight: 600 }}>추가</button>
                  <button onClick={() => setShowAddCl(false)} style={{ fontSize: 12, padding: "5px 12px", border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--text-2)", cursor: "pointer" }}>취소</button>
                </div>
              </div>
            ) : null}

            {/* Comments */}
            <div>
              <SectionHeader icon="💬" title={`댓글 ${detail.comments.length > 0 ? `(${detail.comments.length})` : ""}`} />
              <CommentsSection
                todoId={todoId}
                comments={detail.comments}
                users={dir}
                currentUserId={currentUserId}
                onUpdate={comments => setDetail(prev => prev ? { ...prev, comments } : prev)}
              />
            </div>
          </div>

          {/* Sidebar actions */}
          <div style={{ width: 168, flexShrink: 0, borderLeft: "1px solid var(--border)", padding: "16px 12px", overflowY: "auto", background: "var(--bg-1)" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: .6, marginBottom: 8 }}>카드에 추가</div>

            {/* Labels */}
            <div style={{ marginBottom: 4 }}>
              <button onClick={() => setShowLabels(v => !v)} style={sideBtn}>🏷️ 라벨</button>
              {showLabels && (
                <div style={{ marginTop: 4, background: "var(--bg-0)", border: "1px solid var(--border)", borderRadius: 6, padding: 6 }}>
                  {labels.map(lb => (
                    <label key={lb.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 6px", cursor: "pointer", borderRadius: 4 }}
                      onMouseEnter={e => (e.currentTarget.style.background = "var(--hover)")}
                      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                      onContextMenu={e => { e.preventDefault(); renameLabel(lb); }}
                      title="우클릭하여 이름 수정"
                    >
                      <input type="checkbox" checked={detail.label_ids.includes(lb.id)} onChange={() => toggleLabel(lb.id)} style={{ accentColor: lb.color }} />
                      <span onContextMenu={e => { e.preventDefault(); e.stopPropagation(); renameLabel(lb); }} title="우클릭하여 이름 수정"
                        style={{ width: 12, height: 12, borderRadius: 3, background: lb.color, flexShrink: 0, cursor: "context-menu" }} />
                      <span style={{ fontSize: 11, color: "var(--text-1)" }}>{lb.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Checklist */}
            <button onClick={() => setShowAddCl(v => !v)} style={sideBtn}>☑️ 체크리스트</button>

            {/* Cover */}
            <div style={{ marginBottom: 4 }}>
              <button onClick={() => setShowCover(v => !v)} style={sideBtn}>🎨 커버</button>
              {showCover && (
                <div style={{ marginTop: 4, display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4 }}>
                  {COVER_COLORS.map(c => (
                    <button key={c} onClick={() => { setCover(c); setShowCover(false); }}
                      style={{ width: "100%", aspectRatio: "1", border: detail.cover_color === c ? "2px solid var(--text-1)" : "2px solid transparent", borderRadius: 4, background: c, cursor: "pointer" }}
                    />
                  ))}
                  {detail.cover_color && (
                    <button onClick={() => { setCover(null); setShowCover(false); }}
                      style={{ gridColumn: "1/-1", fontSize: 10, padding: "3px 0", border: "1px solid var(--border)", borderRadius: 4, background: "none", color: "var(--text-3)", cursor: "pointer" }}>
                      커버 제거
                    </button>
                  )}
                </div>
              )}
            </div>

            <div style={{ borderTop: "1px solid var(--border)", marginTop: 12, paddingTop: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: .6, marginBottom: 8 }}>액션</div>

              {isOwner && (
                <button onClick={toggleArchive} style={{ ...sideBtn, color: detail.is_archived ? "#16A34A" : "var(--text-2)" }}>
                  📦 {detail.is_archived ? "보관 해제" : "보관"}
                </button>
              )}
              {isOwner && (
                <button onClick={removeCard} style={{ ...sideBtn, color: "#DC2626" }}>
                  🗑️ 카드 삭제
                </button>
              )}
            </div>
          </div>
        </div>
        {/* 저장 / 취소 — '저장'을 눌러야만 저장됨(딴 곳 클릭·Esc는 변경 폐기) */}
        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end", flexShrink: 0, background: "var(--bg-0)" }}>
          <span style={{ flex: 1, fontSize: 11, color: "var(--text-3)" }}>변경 후 저장을 눌러야 적용됩니다.</span>
          <button className="t-btn" onClick={onClose} disabled={savingCard}>취소</button>
          <button className="t-btn t-btn-primary" onClick={saveCard} disabled={savingCard}>{savingCard ? "저장 중…" : "저장"}</button>
        </div>
      </div>

      {showOrgPicker && (
        <OrgPicker multi excludeIds={detail.assignee_ids} title="담당자 — 조직도에서 선택"
          onClose={() => setShowOrgPicker(false)}
          onPick={(picked) => { applyAssignees([...new Set([...detail.assignee_ids, ...picked.map(p => p.id)])]); setShowOrgPicker(false); }} />
      )}
    </div>
  );
}

// ── Style helper ──────────────────────────────────────────────────────────────

const sideBtn: React.CSSProperties = {
  display: "block", width: "100%", textAlign: "left",
  fontSize: 12, padding: "6px 8px", marginBottom: 4,
  border: "none", borderRadius: 5, background: "var(--bg-2, #e5e7eb)",
  color: "var(--text-2)", cursor: "pointer", fontFamily: "inherit",
};
